import { Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { Check, ChevronDown } from 'lucide-react';
import { useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

interface MultiSelectDropdownProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: readonly string[];
  placeholder: string;
  id?: string;
  disabled?: boolean;
  /**
   * `compact` renders a content-hugging, borderless-ish pill (used inline in the
   * Schema Properties row); the default renders a full-width filled form field
   * (used in the create modal).
   */
  compact?: boolean;
}

/** How many selected chips to render before collapsing the rest into "+N". */
const VISIBLE_CHIPS = 4;
/** Compact (inline) triggers stay on one line — cap tighter so they never wrap. */
const VISIBLE_CHIPS_COMPACT = 3;

/**
 * Popover-backed multi-select dropdown: a trigger showing selected values as
 * chips, and a checkable option list. Modeled on `LanguageMultiSelect` so it
 * behaves consistently with the rest of the app.
 */
export function MultiSelectDropdown({
  value,
  onChange,
  options,
  placeholder,
  id,
  disabled,
  compact,
}: MultiSelectDropdownProps) {
  const { t } = useTranslation('knowledge');
  const [query, setQuery] = useState('');
  /*
   * The option buttons, in render order, so the arrow keys can move focus
   * between them. A ref list rather than a stored "active index": focus is the
   * source of truth here, so there is no second copy of it to fall out of sync
   * when the filter changes the list out from under it.
   */
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const maxVisible = compact ? VISIBLE_CHIPS_COMPACT : VISIBLE_CHIPS;
  const visible = value.slice(0, maxVisible);
  const overflow = value.length - visible.length;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options;
  }, [options, query]);

  /** Moves focus `delta` places from `from`, where `-1` is the filter input. */
  const moveFocus = (from: number, delta: number) => {
    /*
     * Sliced to the current match count before use. A narrowing filter unmounts
     * buttons but leaves their entries behind, so the tail of this array can
     * point at rows that are no longer rendered — reading it whole would let the
     * arrows walk into them. Sliced here rather than trimmed during render,
     * which would be a ref mutation in the render body.
     */
    const buttons = optionRefs.current
      .slice(0, matches.length)
      .filter((el): el is HTMLButtonElement => el !== null);
    if (buttons.length === 0) {
      return;
    }
    /*
     * Wraps at both ends, and `from === -1` (the filter input) has to wrap too:
     * a plain modulo sends ArrowUp from -1 to the second-to-last option rather
     * than the last, which is the sort of off-by-one nobody notices by eye.
     */
    const target = from + delta;
    const next = target < 0 ? buttons.length - 1 : target >= buttons.length ? 0 : target;
    buttons[next]?.focus();
  };

  /**
   * ArrowDown/ArrowUp walk the filtered options.
   *
   * Hand-rolled because this is a `Popover` of plain buttons rather than a
   * listbox primitive — Radix's Popover contributes no key handling of its own,
   * so without this the options are reachable only one Tab at a time and the
   * arrow keys do nothing at all.
   */
  const onListKeyDown = (event: ReactKeyboardEvent, index: number) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveFocus(index, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveFocus(index, -1);
    }
    /*
     * Escape is deliberately NOT handled. Radix closes the popover on it, which
     * is what a popover is expected to do, and intercepting it to move focus
     * back to the filter would mean fighting a listener Radix owns for a
     * behaviour nobody asked for.
     */
  };

  const toggle = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          className={clsx(
            'relative flex items-center gap-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-60',
            compact
              ? 'min-h-8 w-fit rounded-md border border-border-light bg-transparent py-1 pl-2 pr-7 hover:border-border-strong'
              : 'min-h-9 w-full rounded-lg border border-border bg-input px-3 py-1.5',
          )}
        >
          <div
            className={clsx(
              'flex flex-1 items-center gap-1.5',
              compact ? 'flex-nowrap pr-1' : 'flex-wrap pr-5',
            )}
          >
            {visible.length === 0 ? (
              <span className="text-sm text-muted-foreground">{placeholder}</span>
            ) : (
              visible.map((v) => (
                <span
                  key={v}
                  className={clsx(
                    'inline-flex shrink-0 items-center whitespace-nowrap rounded-md bg-muted text-foreground',
                    compact ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-0.5 text-xs',
                  )}
                >
                  {v}
                </span>
              ))
            )}
            {overflow > 0 && (
              <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-xs text-foreground">
                +{overflow}
              </span>
            )}
          </div>
          <ChevronDown
            className={clsx(
              'absolute top-1/2 size-4 -translate-y-1/2 text-muted-foreground',
              compact ? 'right-1.5' : 'right-2',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-(--radix-popover-trigger-width) min-w-56 p-1"
        onCloseAutoFocus={() => setQuery('')}
      >
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // -1 so ArrowDown lands on the first option and ArrowUp on the last.
          onKeyDown={(e) => onListKeyDown(e, -1)}
          placeholder={t('taxonomy.multiSelect.searchPlaceholder')}
          className="mb-1 h-8"
        />
        {/*
          `stopPropagation` is what makes the wheel work at all when this dropdown is opened
          from inside a dialog or sheet.

          `PopoverContent` portals to document.body, so the list sits OUTSIDE the dialog, and
          Radix's Dialog holds a `react-remove-scroll` lock whose `shards` cover only the dialog
          subtree. Everything else has its wheel `preventDefault`ed: arrow keys moved through the
          options and the mouse did nothing.

          react-remove-scroll (2.7.2, pinned transitively via radix-ui) listens on `document` in
          the BUBBLE phase, so stopping propagation here means the event never reaches it. That
          phase is the load-bearing assumption, and NO test can hold it: the regression test in
          `MultiSelectDropdown.test.tsx` asserts the stop, which is the half we own and which
          survives a switch to capture even though the scrolling would not. So bumping that
          dependency needs a manual re-check, not a green suite.
        */}
        <div className="max-h-64 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
          {matches.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {t('taxonomy.multiSelect.noMatches')}
            </p>
          )}
          {matches.map((option, index) => {
            const selected = value.includes(option);
            return (
              <button
                key={option}
                type="button"
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                onKeyDown={(e) => onListKeyDown(e, index)}
                onClick={() => toggle(option)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:bg-accent"
              >
                <span className="flex size-4 items-center justify-center">
                  {selected && <Check className="size-3.5" />}
                </span>
                <span className="flex-1">{option}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

MultiSelectDropdown.displayName = 'MultiSelectDropdown';
